// The run log, kept fresh for the rail, Mission Control and the palette.
// Polls faster while something is running and backs off when the tab is hidden;
// status flips raise a toast (and a browser notification when enabled).
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { listRuns } from "@/api/console";
import type { RunRecord } from "@/api/types";
import { useApi } from "@/composables/useApi";
import { useNotifications } from "@/composables/useNotifications";
import { flowLabel, money, prNumber } from "@/lib/format";
import { isToday, needsAttention, runTitle } from "@/lib/runState";
import { runLink, stoppedAtPlan } from "@/lib/tickets";

const FAST_MS = 4000;
const SLOW_MS = 15000;
const HIDDEN_MS = 30000;

export const useRunsStore = defineStore("runs", () => {
  const runs = ref<RunRecord[]>([]);
  const loaded = ref(false);
  const error = ref("");
  const lastFetch = ref(0);
  // Bumped when an ack happens so the attention list recomputes.
  const ackTick = ref(0);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let started = false;
  const optimistic = new Map<string, RunRecord>();

  const active = computed(() => runs.value.filter((r) => r.status === "running"));
  const attention = computed(() => {
    void ackTick.value;
    return runs.value.filter(needsAttention);
  });
  const doneToday = computed(() => runs.value.filter((r) => r.status === "success" && isToday(r.finished_at ?? r.created_at)));
  const failedToday = computed(() => runs.value.filter((r) => r.status === "failure" && isToday(r.finished_at ?? r.created_at)));
  const spendToday = computed(() =>
    runs.value
      .filter((r) => isToday(r.created_at) && typeof r.cost_usd === "number")
      .reduce((sum, r) => sum + (r.cost_usd ?? 0), 0),
  );

  // Notification links open the run's ticket on the view that answers "what happened".
  function hrefOf(r: RunRecord, view: "changes" | "activity" | "overview"): string {
    const to = runLink(r, stoppedAtPlan(r) && view === "changes" ? "overview" : view);
    const q = new URLSearchParams(to.query).toString();
    return q ? `${to.path}?${q}` : to.path;
  }

  function announce(prev: RunRecord, next: RunRecord): void {
    if (prev.status !== "running" || next.status === "running") return;
    const { notify } = useNotifications();
    const title = `${runTitle(next)} · ${flowLabel(next.flow)}`;
    if (next.status === "success") {
      const bits = [prNumber(next.pr_url) ? `PR ${prNumber(next.pr_url)}` : "finished",
        next.risk_level ? `risk ${next.risk_level}` : "", typeof next.cost_usd === "number" ? money(next.cost_usd) : ""];
      notify({ title, body: bits.filter(Boolean).join(" · "), tone: "good", href: hrefOf(next, "changes") });
    } else {
      notify({ title, body: next.error_step ? `failed at ${next.error_step}` : "failed", tone: "bad", href: hrefOf(next, "activity") });
    }
  }

  async function refresh(): Promise<void> {
    const { client } = useApi();
    try {
      const fresh = await listRuns(client, 100);
      const prevById = new Map(runs.value.map((r) => [r.job_id, r]));
      for (const r of fresh) {
        const prev = prevById.get(r.job_id);
        if (prev && loaded.value) announce(prev, r);
        optimistic.delete(r.job_id);
      }
      runs.value = [...optimistic.values(), ...fresh];
      error.value = "";
      loaded.value = true;
      lastFetch.value = Date.now();
    } catch (e) {
      error.value = (e as { message?: string }).message ?? "failed to load runs";
    }
  }

  function interval(): number {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return HIDDEN_MS;
    return active.value.length ? FAST_MS : SLOW_MS;
  }

  function schedule(): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      await refresh();
      schedule();
    }, interval());
  }

  function start(): void {
    if (started) return;
    started = true;
    void refresh().then(schedule);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void refresh().then(schedule);
      });
    }
  }

  // Show a just-launched run immediately; the next poll replaces it.
  function addOptimistic(r: RunRecord): void {
    optimistic.set(r.job_id, r);
    runs.value = [r, ...runs.value.filter((x) => x.job_id !== r.job_id)];
    void refresh();
  }

  function ack(jobId: string, ackFn: (id: string) => void): void {
    ackFn(jobId);
    ackTick.value++;
  }

  function byId(jobId: string): RunRecord | undefined {
    return runs.value.find((r) => r.job_id === jobId);
  }

  return {
    runs, loaded, error, lastFetch, active, attention, doneToday, failedToday, spendToday,
    refresh, start, addOptimistic, ack, byId,
  };
});
